import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountant } from '../../contexts/AccountantContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { 
  Buildings,
  Receipt,
  CalendarBlank,
  Warning,
  CheckCircle,
  XCircle,
  Clock,
  CircleNotch,
  Plus,
  MagnifyingGlass,
  ArrowRight,
  FileText,
  ChartLineUp,
  Users,
  ShieldCheck,
  CaretRight,
  Bell,
  Funnel,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  Export,
  ArrowSquareOut,
  SignIn,
  CloudArrowUp,
  Timer
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import BackupScheduleManager from '../../components/accountant/BackupScheduleManager';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountantDashboard() {
  const { user } = useAuth();
  const { accountantSettings, branding, hasFeature, enterClientWorkspace, exitClientWorkspace, isInClientContext, contextLoading } = useAccountant();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [enteringClientId, setEnteringClientId] = useState(null);
  
  // Add Client Dialog
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    trn: '',
    industry: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    currency: 'AED',
    country: 'AE',
    timezone: 'Asia/Dubai'
  });
  const [addingClient, setAddingClient] = useState(false);

  // Backup Schedule Dialog
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedClientForSchedule, setSelectedClientForSchedule] = useState(null);

  // Auto-exit client context when visiting the Accountant Hub
  useEffect(() => {
    if (isInClientContext) {
      exitClientWorkspace().catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/accountant/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accountant mode is not enabled');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name) {
      toast.error('Company name is required');
      return;
    }
    
    setAddingClient(true);
    try {
      await axios.post(`${API_URL}/accountant/clients`, newClient);
      toast.success('Client added successfully');
      setShowAddClient(false);
      setNewClient({ name: '', trn: '', industry: '', contact_email: '', contact_phone: '', address: '', currency: 'AED', country: 'AE', timezone: 'Asia/Dubai' });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add client');
    } finally {
      setAddingClient(false);
    }
  };

  const handleEnterClientWorkspace = async (clientId, e) => {
    e.stopPropagation(); // Prevent row click
    setEnteringClientId(clientId);
    try {
      await enterClientWorkspace(clientId);
      // Navigate to dashboard after entering client workspace
      navigate('/dashboard');
    } catch (error) {
      // Error already shown by context
    } finally {
      setEnteringClientId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      not_started: { label: 'Not Started', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
      draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
      in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      ready: { label: 'Ready', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
      filed: { label: 'Filed', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
      validation_failed: { label: 'Failed', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
    };
    
    const config = statusConfig[status] || statusConfig.not_started;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredClients = dashboardData?.client_overview?.filter(client => {
    const matchesSearch = client.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'alerts' && client.alerts > 0) ||
      (statusFilter === 'ready' && client.vat_status === 'ready') ||
      (statusFilter === 'filed' && client.vat_status === 'filed');
    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accountant-dashboard">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <div className="flex items-center gap-3">
            <img src={branding.logo} alt={branding.appName} className="h-12" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight text-[#0f172a] dark:text-white">
                Accountant Dashboard
              </h1>
              <p className="text-muted-foreground text-sm">{branding.subtitle}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge className="bg-[#0f172a] text-white px-3 py-1">
            {accountantSettings?.accountant_plan?.replace('accountant_', '').toUpperCase()} Plan
          </Badge>
          <Button onClick={() => setShowAddClient(true)} data-testid="add-client-btn">
            <Plus size={16} className="mr-2" /> Add Client
          </Button>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-xl p-5 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/10">
              <Buildings size={24} className="text-[#d4a84b]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Total Clients</p>
              <p className="text-3xl font-bold">{dashboardData?.kpis?.total_clients || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/20">
              <Receipt size={24} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-amber-100">VAT Due This Month</p>
              <p className="text-3xl font-bold">{dashboardData?.kpis?.vat_due_this_month || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/20">
              <CheckCircle size={24} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-emerald-100">Returns Ready</p>
              <p className="text-3xl font-bold">{dashboardData?.kpis?.returns_ready || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/20">
              <Warning size={24} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-red-100">Alerts</p>
              <p className="text-3xl font-bold">{dashboardData?.kpis?.alerts || 0}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Overview Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2 card-tactile rounded-xl overflow-hidden"
        >
          <div className="p-4 border-b bg-[#0f172a] text-white">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2">
                <Users size={18} className="text-[#d4a84b]" />
                Client Overview
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search clients..."
                    className="pl-9 h-8 w-48 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 w-32 bg-white/10 border-white/20 text-white">
                    <Funnel size={14} className="mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="alerts">With Alerts</SelectItem>
                    <SelectItem value="ready">Ready to File</SelectItem>
                    <SelectItem value="filed">Filed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="max-h-[400px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-900">
                  <TableHead>Company</TableHead>
                  <TableHead>VAT Status</TableHead>
                  <TableHead>CT Status</TableHead>
                  <TableHead className="text-center">Alerts</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {dashboardData?.client_overview?.length === 0 
                        ? 'No clients yet. Add your first client to get started.'
                        : 'No clients match your search criteria.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow 
                      key={client.company_id} 
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900"
                      onClick={() => navigate(`/dashboard/accountant/client/${client.company_id}`)}
                    >
                      <TableCell className="font-medium">{client.company_name}</TableCell>
                      <TableCell>{getStatusBadge(client.vat_status)}</TableCell>
                      <TableCell>{getStatusBadge(client.ct_status)}</TableCell>
                      <TableCell className="text-center">
                        {client.alerts > 0 ? (
                          <Badge className="bg-red-100 text-red-700">
                            <Warning size={12} className="mr-1" /> {client.alerts}
                          </Badge>
                        ) : (
                          <CheckCircle size={18} className="text-emerald-500 mx-auto" weight="fill" />
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(client.last_activity)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={(e) => handleEnterClientWorkspace(client.client_id, e)}
                            disabled={contextLoading || enteringClientId === client.client_id}
                            className="bg-[#0f172a] hover:bg-[#1e293b]"
                            data-testid={`manage-client-${client.client_id}`}
                          >
                            {enteringClientId === client.client_id ? (
                              <CircleNotch size={14} className="animate-spin mr-1" />
                            ) : (
                              <SignIn size={14} className="mr-1" />
                            )}
                            Manage
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <DotsThree size={16} weight="bold" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/accountant/client/${client.company_id}`);
                                }}
                              >
                                <Eye size={14} className="mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedClientForSchedule({
                                    id: client.client_id,
                                    name: client.company_name
                                  });
                                  setShowScheduleDialog(true);
                                }}
                              >
                                <Timer size={14} className="mr-2" />
                                Schedule Backup
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // TODO: Export client data
                                  toast.info('Export feature coming soon');
                                }}
                              >
                                <Export size={14} className="mr-2" />
                                Export Data
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </motion.div>

        {/* Right Panel - Action Center */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-6"
        >
          {/* Upcoming Deadlines */}
          <div className="card-tactile rounded-xl overflow-hidden">
            <div className="p-4 border-b bg-amber-50 dark:bg-amber-950/30">
              <h3 className="font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <CalendarBlank size={18} weight="fill" />
                Upcoming Deadlines
              </h3>
            </div>
            <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
              {dashboardData?.upcoming_deadlines?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No upcoming deadlines</p>
              ) : (
                dashboardData?.upcoming_deadlines?.map((deadline, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <div>
                      <p className="text-sm font-medium">{deadline.company_name}</p>
                      <p className="text-xs text-muted-foreground">{deadline.tax_type.toUpperCase()} Filing</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-amber-600">{formatDate(deadline.deadline_date)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Alerts & Tasks */}
          <div className="card-tactile rounded-xl overflow-hidden">
            <div className="p-4 border-b bg-red-50 dark:bg-red-950/30">
              <h3 className="font-semibold flex items-center gap-2 text-red-700 dark:text-red-400">
                <Bell size={18} weight="fill" />
                Alerts & Tasks
              </h3>
            </div>
            <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
              {dashboardData?.pending_tasks?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No pending tasks</p>
              ) : (
                dashboardData?.pending_tasks?.map((task, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <div>
                      <p className="text-sm font-medium">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.company_name}</p>
                    </div>
                    <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                      {task.priority}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card-tactile rounded-xl p-4">
            <h3 className="font-semibold mb-3 text-sm">Quick Actions</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/dashboard/accountant/reports')}>
                <FileText size={16} className="mr-2" /> Generate Reports
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/dashboard/accountant/documents')}>
                <Export size={16} className="mr-2" /> View All Documents
              </Button>
              {hasFeature('bulk_operations') && (
                <Button variant="outline" className="w-full justify-start">
                  <ChartLineUp size={16} className="mr-2" /> Bulk Validate
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Buildings size={20} />
              Add New Client
            </DialogTitle>
            <DialogDescription>
              Add a new client company to manage their tax compliance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient(p => ({ ...p, name: e.target.value }))}
                placeholder="Enter company name"
                data-testid="new-client-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Country *</Label>
                <Select
                  value={newClient.country}
                  onValueChange={(v) => {
                    const countryConfig = {
                      'AE': { currency: 'AED', timezone: 'Asia/Dubai' },
                      'SA': { currency: 'SAR', timezone: 'Asia/Riyadh' },
                      'QA': { currency: 'QAR', timezone: 'Asia/Qatar' },
                      'KW': { currency: 'KWD', timezone: 'Asia/Kuwait' },
                      'BH': { currency: 'BHD', timezone: 'Asia/Bahrain' },
                      'OM': { currency: 'OMR', timezone: 'Asia/Muscat' },
                      'IN': { currency: 'INR', timezone: 'Asia/Kolkata' },
                      'GB': { currency: 'GBP', timezone: 'Europe/London' },
                      'US': { currency: 'USD', timezone: 'America/New_York' },
                      'EU': { currency: 'EUR', timezone: 'Europe/Paris' }
                    };
                    const config = countryConfig[v] || { currency: 'USD', timezone: 'UTC' };
                    setNewClient(p => ({ ...p, country: v, currency: config.currency, timezone: config.timezone }));
                  }}
                >
                  <SelectTrigger data-testid="new-client-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AE">UAE</SelectItem>
                    <SelectItem value="SA">Saudi Arabia</SelectItem>
                    <SelectItem value="QA">Qatar</SelectItem>
                    <SelectItem value="KW">Kuwait</SelectItem>
                    <SelectItem value="BH">Bahrain</SelectItem>
                    <SelectItem value="OM">Oman</SelectItem>
                    <SelectItem value="IN">India</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="EU">European Union</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Currency *</Label>
                <Select
                  value={newClient.currency}
                  onValueChange={(v) => setNewClient(p => ({ ...p, currency: v }))}
                >
                  <SelectTrigger data-testid="new-client-currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                    <SelectItem value="SAR">SAR - Saudi Riyal</SelectItem>
                    <SelectItem value="QAR">QAR - Qatari Riyal</SelectItem>
                    <SelectItem value="KWD">KWD - Kuwaiti Dinar</SelectItem>
                    <SelectItem value="BHD">BHD - Bahraini Dinar</SelectItem>
                    <SelectItem value="OMR">OMR - Omani Rial</SelectItem>
                    <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>TRN (Tax Registration Number)</Label>
              <Input
                value={newClient.trn}
                onChange={(e) => setNewClient(p => ({ ...p, trn: e.target.value }))}
                placeholder="100-XXXX-XXXX-XXX"
                data-testid="new-client-trn"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input
                  value={newClient.industry}
                  onChange={(e) => setNewClient(p => ({ ...p, industry: e.target.value }))}
                  placeholder="e.g., Retail"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  value={newClient.contact_email}
                  onChange={(e) => setNewClient(p => ({ ...p, contact_email: e.target.value }))}
                  placeholder="email@company.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contact Phone</Label>
              <Input
                value={newClient.contact_phone}
                onChange={(e) => setNewClient(p => ({ ...p, contact_phone: e.target.value }))}
                placeholder="+971 XX XXX XXXX"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClient(false)}>Cancel</Button>
            <Button onClick={handleAddClient} disabled={addingClient}>
              {addingClient ? <CircleNotch className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-lg">
          {selectedClientForSchedule && (
            <BackupScheduleManager
              clientId={selectedClientForSchedule.id}
              clientName={selectedClientForSchedule.name}
              onClose={() => setShowScheduleDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
